// ABOUTME: React Query hook for fetching operational hours configuration from the backend API.
// ABOUTME: Calls GET /tuc-configs/operational-hours, finds config for selected scope, and transforms to form-ready shape.

import {useMemo} from 'react'
import {useQuery} from '@tanstack/react-query'

import {api} from '../../../../App.store'
import {
  DAY_CODE_TO_ID,
  DayCode,
  DayIdentifier,
} from '../types/deliveryWindow'
import {
  OperationalHoursApiResponse,
  OperationalHoursConfig,
  OperationalHoursFormValues,
} from '../types/operationalHours'
import {asNotInherited, emptyApiRow} from '../utils/emptyConfig'

/** Strips seconds from API time string: "07:30:00" → "07:30" */
const toDisplayTime = (apiTime: string): string => apiTime.slice(0, 5)

const transformResponse = (response: OperationalHoursApiResponse): OperationalHoursConfig => {
  const timeZone = response.timeZone?.value ?? ''
  const businessDays = (response.businessDays?.value?.activeDays ?? [])
    .map((code) => DAY_CODE_TO_ID[code as DayCode])
    .filter((d): d is DayIdentifier => Boolean(d))
  const standardDays = (response.operatingSchedule?.value?.standard?.standardDays ?? [])
    .map((code) => DAY_CODE_TO_ID[code as DayCode])
    .filter((d): d is DayIdentifier => Boolean(d))
  const standardHours = {
    opens: toDisplayTime(response.operatingSchedule?.value?.standard?.opens ?? ''),
    prePlanning: toDisplayTime(response.operatingSchedule?.value?.standard?.prePlanning ?? ''),
    closes: toDisplayTime(response.operatingSchedule?.value?.standard?.closes ?? ''),
  }
  const nonStandardHours = (response.operatingSchedule?.value?.nonStandard ?? []).reduce(
    (acc, ns) => {
      const dayId = DAY_CODE_TO_ID[ns.day as DayCode]
      if (dayId) {
        acc[dayId] = {
          opens: toDisplayTime(ns.opens ?? ''),
          prePlanning: toDisplayTime(ns.prePlanning ?? ''),
          closes: toDisplayTime(ns.closes ?? ''),
        }
      }
      return acc
    },
    {} as Record<string, {opens: string; prePlanning: string; closes: string}>
  )
  const nonWorkingDays = response.nonWorkingDays?.value ?? []
  const formValues: OperationalHoursFormValues = {timeZone, businessDays, standardDays, standardHours, nonStandardHours, nonWorkingDays}
  return {
    id: response.id,
    ...formValues,
    overrides: {
      timeZone: response.timeZone?.isOverridden ?? false,
      businessDays: response.businessDays?.isOverridden ?? false,
      operatingSchedule: response.operatingSchedule?.isOverridden ?? false,
      nonWorkingDays: response.nonWorkingDays?.isOverridden ?? false,
    },
    parentValues: formValues,
    _meta: {
      scopeId: response.scopeId,
      isInherited: !(response.timeZone?.isOverridden ?? false),
      resolvedFrom: null,
      createdAt: response._meta.createdAt,
      updatedAt: response._meta.updatedAt,
      createdBy: response._meta.createdBy,
      updatedBy: response._meta.updatedBy,
    },
  }
}

export const useOperationalHoursConfig = (
  countryId: string | undefined,
  businessLine: string | undefined,
  scopeId: string | undefined,
  rootScopeId?: string | undefined
): {data: OperationalHoursConfig | undefined; isLoading: boolean; error: Error | null} => {
  const {data: rawData, isLoading, error} = useQuery<OperationalHoursApiResponse[], Error>({
    queryKey: ['operationalHoursConfig', countryId, businessLine],
    queryFn: async () => {
      const res = await api.get<OperationalHoursApiResponse[]>('/tuc-configs/operational-hours', {
        params: {countryId, businessLine: businessLine!.toUpperCase()},
      })
      return res.data ?? []
    },
    enabled: Boolean(countryId && businessLine && scopeId),
    staleTime: 30_000,
  })

  const data = useMemo(() => {
    if (!rawData || !scopeId) return undefined

    const rootRawConfig = rootScopeId
      ? rawData.find((r) => r.scopeId === rootScopeId)
      : undefined
    const rootConfig = rootRawConfig ? transformResponse(rootRawConfig) : undefined
    const parentValues = rootConfig
      ? {
          timeZone: rootConfig.timeZone,
          businessDays: rootConfig.businessDays,
          standardDays: rootConfig.standardDays,
          standardHours: rootConfig.standardHours,
          nonStandardHours: rootConfig.nonStandardHours,
          nonWorkingDays: rootConfig.nonWorkingDays,
        }
      : undefined

    // Direct match — this scope has its own config
    const match = rawData.find((r) => r.scopeId === scopeId)
    if (match) {
      const config = transformResponse(match)
      return parentValues ? {...config, parentValues} : config
    }

    // No direct config — show inherited values from root/parent
    if (!rootRawConfig || !rootConfig) {
      return asNotInherited(transformResponse(emptyApiRow<OperationalHoursApiResponse>(scopeId)))
    }

    return {
      ...rootConfig,
      id: undefined,
      parentValues: parentValues ?? {
        timeZone: rootConfig.timeZone,
        businessDays: rootConfig.businessDays,
        standardDays: rootConfig.standardDays,
        standardHours: rootConfig.standardHours,
        nonStandardHours: rootConfig.nonStandardHours,
        nonWorkingDays: rootConfig.nonWorkingDays,
      },
      _meta: {...rootConfig._meta, isInherited: true},
      overrides: {
        timeZone: false,
        businessDays: false,
        operatingSchedule: false,
        nonWorkingDays: false,
      },
    }
  }, [rawData, scopeId, rootScopeId])

  return {data, isLoading, error}
}
